import { CampaignRecipient } from "../../features/campaign/campaignTypes";
import { Price } from "../../features/interfaces";

export type PriceSelectOption = {
  label: string;
  value: string;
};

function isDefaultPrice(price: Price) {
  return Boolean(price.is_default);
}

export function priceOptionsForGiftType(
  prices: Price[] | undefined,
  giftType?: string,
): PriceSelectOption[] {
  if (!prices?.length || !giftType) return [];
  return prices
    .filter((price) => String(price.gift_types || "").includes(giftType))
    .map((price) => ({
      value: price.price_id,
      label: `$${price.order_price}`,
    }));
}

/** Prefer saved id, else is_default price, else first option. */
export function resolveDefaultPriceId(
  prices: Price[] | undefined,
  giftType?: string,
  preferredId?: string | null,
): string {
  const options = priceOptionsForGiftType(prices, giftType);
  if (!options.length || !giftType) return "";
  if (preferredId && options.some((o) => o.value === preferredId)) {
    return preferredId;
  }
  const defaultPrice = prices?.find(
    (price) =>
      String(price.gift_types || "").includes(giftType) && isDefaultPrice(price),
  );
  if (defaultPrice) return defaultPrice.price_id;
  return options[0].value;
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export function formatEventMmDd(
  value?: string | Date | null,
  options?: { includeYear?: boolean },
): string {
  if (!value) return "—";
  const raw = String(value);
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const [, year, month, day] = match;
    if (options?.includeYear) {
      return `${month}/${day}/${year}`;
    }
    return `${month}/${day}`;
  }
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(raw)) {
    const [month, day, year] = raw.split("/");
    return options?.includeYear ? `${month}/${day}/${year}` : `${month}/${day}`;
  }
  if (/^\d{1,2}\/\d{1,2}$/.test(raw)) return raw;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "—";
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  if (options?.includeYear) {
    const yyyy = d.getUTCFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }
  return `${mm}/${dd}`;
}

export function monthOrderFromCurrent(monthIndex: number, startMonth = new Date().getMonth()) {
  return (monthIndex - startMonth + 12) % 12;
}

export function groupRecipientsByMonth(
  recipients: CampaignRecipient[],
  startMonth = new Date().getMonth(),
) {
  const groups = new Map<number, CampaignRecipient[]>();
  const noEventDate: CampaignRecipient[] = [];

  for (const recipient of recipients) {
    if (!recipient.event_date) {
      noEventDate.push(recipient);
      continue;
    }
    const raw = String(recipient.event_date);
    const match = raw.match(/^\d{4}-(\d{2})-/);
    let monthIndex = match
      ? Number(match[1]) - 1
      : new Date(raw).getUTCMonth();
    if (Number.isNaN(monthIndex) || monthIndex < 0 || monthIndex > 11) {
      noEventDate.push(recipient);
      continue;
    }
    if (!groups.has(monthIndex)) groups.set(monthIndex, []);
    groups.get(monthIndex)!.push(recipient);
  }

  const grouped = Array.from(groups.entries())
    .sort(
      ([a], [b]) =>
        monthOrderFromCurrent(a, startMonth) - monthOrderFromCurrent(b, startMonth),
    )
    .map(([monthIndex, list]) => ({
      monthIndex,
      label: MONTH_NAMES[monthIndex],
      recipients: list.sort((a, b) =>
        formatEventMmDd(a.event_date).localeCompare(formatEventMmDd(b.event_date)),
      ),
    }));

  if (noEventDate.length) {
    grouped.push({
      monthIndex: -1,
      label: "No event date",
      recipients: noEventDate.sort((a, b) =>
        (a.name || a.email).localeCompare(b.name || b.email),
      ),
    });
  }

  return grouped;
}

const AUTOMATED_OCCASION_IDS = {
  birthday: "b6558d9b-0792-4e0e-be1e-816ff88ec7bf",
  "home-anniversary": "0bb206cd-a89b-4a37-ac58-31c00a17d3fb",
} as const;

export function occasionDefaultNote(
  occasions:
    | Array<{
        occasion_id?: string;
        handwritten_note?: string | null;
      }>
    | undefined,
  tab: "birthday" | "home-anniversary",
): string {
  const occasionId = AUTOMATED_OCCASION_IDS[tab];
  const match = (occasions || []).find(
    (occasion) => occasion.occasion_id === occasionId,
  );
  return match?.handwritten_note?.trim() || "";
}

function isDeletedFlag(value: unknown) {
  return value === true || value === 1 || value === "1";
}

function isInactiveFlag(value: unknown) {
  return value === false || value === 0 || value === "0";
}

export function isRecipientActive(recipient: CampaignRecipient) {
  return !isInactiveFlag(recipient.is_active as unknown);
}

/** Non-deleted recipients, including inactive ones. */
export function visibleRecipients(list?: CampaignRecipient[] | null) {
  return (list || []).filter((r) => !isDeletedFlag(r.is_delete as unknown));
}

export function activeRecipients(list?: CampaignRecipient[] | null) {
  return visibleRecipients(list).filter((r) => isRecipientActive(r));
}
